import { useEffect, useState } from 'react';
import { useSelector } from 'react-redux';
import { useHistory } from 'react-router-dom';

import { submitCaseToSF } from '../../../actions/hub/hubContactPageActions';
import { Enrolment } from '../../../api/types/enrolment';
import ErrorBlock from '../../../common/ErrorBlock';
import { ContainerLayout } from '../../../common/hub/ContainerLayout';
import withSelectedEnrolment from '../../../common/hub/NewWithSelectedEnrolment';
import { RestrictedButton } from '../../../components/RestrictedButton/RestrictedButton';
import { ShadowContainer } from '../../../components/ShadowContainer/ShadowContainer';
import { TextArea, useTextAreaProps } from '../../../components/TextArea/TextArea';
import FormSubHeading from '../../../components/hub/contact/FormSubHeading';
import { useActions } from '../../../util/hooks/useActions';
import { PATH_HUB_CR_OTHER_QUERY, PATH_HUB_CR_SUBMIT_CASE_SUCCESS } from '../../../util/pagePath';
import { usePageHeader } from '../../../v2/app-shell/usePageHeader';
import { StudentEnrolmentDetails } from './StudentEnrolmentDetails';

interface Props {
  selectedEnrolment: Enrolment;
}

const FormOtherQuery = ({ selectedEnrolment }: Props) => {
  const { setHeader } = usePageHeader();

  useEffect(() => {
    setHeader({
      label: 'Report an issue',
      showIcon: false,
      showBackButton: true,
    });
  }, [setHeader]);

  const { isSubmitting, submitError, errorMsg } = useSelector(
    (state: {
      ui: { apiState: { hubContactPage: { isLoading: boolean; fetchError: boolean; errorMsg: string } } };
    }) => {
      const isSubmitting = state.ui.apiState.hubContactPage.isLoading;
      const submitError = state.ui.apiState.hubContactPage.fetchError;
      const errorMsg = state.ui.apiState.hubContactPage.errorMsg;
      return { isSubmitting, submitError, errorMsg };
    }
  );

  const submit = useActions(submitCaseToSF);
  const history = useHistory();
  const { enrolmentId, studentSfid, student, subject } = selectedEnrolment;

  const textAreaProps = useTextAreaProps({
    id: 'text-how-can-we-help',
    labelText: 'How can we help you?',
  });

  const message = textAreaProps.value;
  const [loading, setLoading] = useState(false);
  const onClickHandler = () => {
    setLoading(true);
    submit({
      uniqueEnrolmentId: enrolmentId,
      message: `Details: ${message}`,
      caseSubject: 'Contact request - Feedback - Other',
      caseType: 'Feedback',
      caseCategory: 'Other',
      onSubmitSuccess: () => {
        history.push(PATH_HUB_CR_SUBMIT_CASE_SUCCESS, {
          firstName: student,
          studentId: studentSfid,
          message: `Your request for ${student}'s ${subject} enrolment has been submitted successfully. We'll be in touch soon.`,
        });
      },
    }).finally(() => {
      setLoading(false);
    });
  };

  return (
    <ContainerLayout>
      <ShadowContainer className="flex flex-col gap-y-10 !rounded-3xl bg-white p-6 md:!p-20">
        <div className="flex flex-col gap-y-5">
          <FormSubHeading />
          {submitError && errorMsg && <ErrorBlock errorMsg={errorMsg} />}
          <StudentEnrolmentDetails student={student} subject={subject} selectedEnrolment={selectedEnrolment} />
        </div>
        <form>
          <TextArea {...textAreaProps} className="mb-7" disabled={loading} />
        </form>
        <div className="mb-4 text-center tracking-normal lg:mb-8">
          <RestrictedButton
            buttonText={loading ? 'Submitting' : 'Submit query'}
            disabled={loading}
            truthyChecks={[message.length > 0, !isSubmitting]}
            onClick={() => {
              onClickHandler();
            }}
          />
        </div>
      </ShadowContainer>
    </ContainerLayout>
  );
};
export default withSelectedEnrolment(FormOtherQuery, null, PATH_HUB_CR_OTHER_QUERY);
